import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  ComposedChart, Bar, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
} from "recharts";
import { Plus, X, Lock, Unlock, Download, Upload, Trash2 } from "lucide-react";
import { cmdApi } from "../api";
import { usePermissions } from "../hooks/usePermissions";
import { formatINR, fmtDate } from "../format";
import { computeEntry, fmtPct, deltaPct } from "../fincalc";

const MONEY_FIELDS = [
  ["revenue", "Revenue"], ["cogs", "COGS"], ["staff_cost", "Staff cost"], ["rent", "Rent"],
  ["utilities", "Utilities"], ["marketing", "Marketing"], ["other_opex", "Other opex"],
];
const BALANCE_FIELDS = [["cash_balance", "Cash balance"], ["receivables", "Receivables"], ["payables", "Payables"], ["capex", "Capex"]];
const BUDGET_FIELDS = [["budget_revenue", "Budget revenue"], ["budget_ebitda", "Budget EBITDA"]];

function istLastMonth() {
  const now = new Date(new Date().toLocaleString("en-US", { timeZone: "Asia/Kolkata" }));
  const m = now.getMonth(); // 0-based; last month:
  const y = m === 0 ? now.getFullYear() - 1 : now.getFullYear();
  const mm = m === 0 ? 12 : m;
  return `${y}-${String(mm).padStart(2, "0")}`;
}

function Sheet({ onClose, title, children, testId, wide }) {
  return (
    <>
      <div className="cmd-sheet-backdrop" onClick={onClose} />
      <div className="cmd-sheet" style={wide ? { width: "min(860px, 94vw)" } : undefined}
        role="dialog" aria-modal="true" data-testid={testId}>
        <div className="cmd-sheet-head">
          <h2>{title}</h2>
          <button className="cmd-icon-btn" onClick={onClose} aria-label="Close"><X size={16} /></button>
        </div>
        <div className="cmd-sheet-body">{children}</div>
      </div>
    </>
  );
}

function Kpi({ label, value, sub, subNeg, testId }) {
  return (
    <div className="cmd-kpi" data-testid={testId}>
      <div className="cmd-kpi-value" style={{ fontSize: 22 }}>{value}</div>
      <div className="cmd-kpi-label">{label}</div>
      {sub && <div className={`cmd-kpi-sub ${subNeg ? "neg" : ""}`}>{sub}</div>}
    </div>
  );
}

function EbitdaDot(props) {
  const { cx, cy, payload } = props;
  if (payload.ebitda === null || payload.ebitda === undefined) return null;
  return <circle cx={cx} cy={cy} r={4} fill={payload.ebitda >= 0 ? "var(--cmd-health-ok)" : "var(--cmd-health-risk)"} stroke="none" />;
}

function EntryForm({ initiatives, initId, month, existing, canEdit, onSaved, onClose, onDelete }) {
  const [fInit, setFInit] = useState(initId || initiatives[0]?.id || "");
  const [fMonth, setFMonth] = useState(month);
  const [vals, setVals] = useState({});
  const [busy, setBusy] = useState(false);
  const [loadingEntry, setLoadingEntry] = useState(false);

  // Load the record whenever initiative or month changes.
  useEffect(() => {
    if (!fInit || !fMonth) return;
    setLoadingEntry(true);
    (async () => {
      try {
        const { data } = await cmdApi.get(`/financials?month=${fMonth}&scope=${fInit}`);
        const row = data.rows.find((r) => r.initiative_id === fInit);
        const next = {};
        for (const [k] of [...MONEY_FIELDS, ...BALANCE_FIELDS, ...BUDGET_FIELDS]) {
          next[k] = row && row[k] !== null && row[k] !== undefined ? String(row[k]) : "";
        }
        next.notes = row?.notes || "";
        setVals(next);
      } catch { /* keep blanks */ }
      setLoadingEntry(false);
    })();
  }, [fInit, fMonth]);

  const live = computeEntry(vals);
  const set = (k, v) => setVals((s) => ({ ...s, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    const payload = {};
    for (const [k] of [...MONEY_FIELDS, ...BALANCE_FIELDS, ...BUDGET_FIELDS]) {
      const v = vals[k];
      if (v !== "" && v !== undefined) {
        const num = Number(String(v).replace(/[₹,\s]/g, ""));
        if (!Number.isNaN(num)) payload[k] = num;
      }
    }
    if (vals.notes) payload.notes = vals.notes;
    try {
      await cmdApi.put(`/financials/${fInit}/${fMonth}`, payload);
      toast.success("Figures saved");
      onSaved();
    } catch (ex) {
      toast.error(ex?.response?.data?.detail || "Could not save figures.");
    } finally { setBusy(false); }
  };

  const numInput = (k, label) => (
    <div className="cmd-field" key={k}>
      <label>{label}</label>
      <input className="cmd-input" inputMode="decimal" value={vals[k] ?? ""} disabled={!canEdit}
        onChange={(e) => set(k, e.target.value)} placeholder="0"
        data-testid={`fin-input-${k}`} />
    </div>
  );

  return (
    <form onSubmit={submit} data-testid="fin-entry-form">
      <div className="cmd-form-grid">
        <div className="cmd-field">
          <label>Initiative</label>
          <select className="cmd-input" value={fInit} onChange={(e) => setFInit(e.target.value)} data-testid="fin-form-initiative">
            {initiatives.map((i) => <option key={i.id} value={i.id}>{i.name}</option>)}
          </select>
        </div>
        <div className="cmd-field">
          <label>Month</label>
          <input type="month" className="cmd-input" value={fMonth} onChange={(e) => setFMonth(e.target.value)} data-testid="fin-form-month" />
        </div>
      </div>
      {loadingEntry && <p className="cmd-muted" style={{ fontSize: 13 }}>Loading existing figures…</p>}

      <div className="cmd-group-head">Income and costs</div>
      <div className="cmd-form-grid">{MONEY_FIELDS.slice(0, 3).map(([k, l]) => numInput(k, l))}</div>
      <div className="cmd-form-grid">{MONEY_FIELDS.slice(3).map(([k, l]) => numInput(k, l))}</div>

      <div className="cmd-group-head">Balances at month end</div>
      <div className="cmd-form-grid">{BALANCE_FIELDS.map(([k, l]) => numInput(k, l))}</div>

      <div className="cmd-group-head">Budget</div>
      <div className="cmd-form-grid">{BUDGET_FIELDS.map(([k, l]) => numInput(k, l))}</div>

      <div className="cmd-field">
        <label>Notes</label>
        <textarea className="cmd-input" rows={2} value={vals.notes ?? ""} disabled={!canEdit}
          onChange={(e) => set("notes", e.target.value)} data-testid="fin-input-notes" />
      </div>

      <div className="cmd-live-line" data-testid="fin-live-line">
        Gross margin {fmtPct(live.gm_pct)} · Opex {formatINR(live.opex)} · EBITDA {formatINR(live.ebitda)}
        {live.ebitda_pct !== null ? ` (${fmtPct(live.ebitda_pct)})` : ""}
      </div>

      {canEdit && (
        <div style={{ display: "flex", gap: 10, justifyContent: "space-between", marginTop: 14 }}>
          <div>
            {onDelete && (
              <button type="button" className="cmd-btn cmd-btn-ghost"
                style={{ color: "var(--cmd-health-risk)" }}
                onClick={() => {
                  const sel = initiatives.find((i) => i.id === fInit);
                  onDelete(fInit, sel?.name || "this initiative");
                }}
                data-testid="fin-form-delete">
                <Trash2 size={14} style={{ marginRight: 4 }} /> Remove this row…
              </button>
            )}
          </div>
          <div style={{ display: "flex", gap: 10 }}>
            <button type="button" className="cmd-btn cmd-btn-ghost" onClick={onClose}>Cancel</button>
            <button type="submit" className="cmd-btn cmd-btn-primary" disabled={busy} data-testid="fin-form-save">
              {busy ? "Saving…" : "Save figures"}
            </button>
          </div>
        </div>
      )}
    </form>
  );
}

function ImportSheet({ month, onDone, onClose }) {
  const [text, setText] = useState("");
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);

  const run = async (confirm) => {
    setBusy(true);
    try {
      const { data } = await cmdApi.post("/financials/import", { csv_text: text, confirm });
      if (confirm) {
        toast.success(`Imported ${data.written} row${data.written === 1 ? "" : "s"}`);
        onDone();
      } else {
        setPreview(data);
      }
    } catch (ex) {
      toast.error(ex?.response?.data?.detail || "Import failed.");
    } finally { setBusy(false); }
  };

  const onFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const rd = new FileReader();
    rd.onload = () => setText(String(rd.result || ""));
    rd.readAsText(file);
  };

  return (
    <div data-testid="import-sheet">
      <div className="cmd-field">
        <label>Paste CSV or pick a file</label>
        <input type="file" accept=".csv,text/csv" onChange={onFile} data-testid="import-file" style={{ marginBottom: 8 }} />
        <textarea className="cmd-input" rows={7} value={text} onChange={(e) => setText(e.target.value)}
          placeholder="initiative,month,revenue,cogs,…" data-testid="import-text" />
      </div>
      <div style={{ display: "flex", gap: 10 }}>
        <button className="cmd-btn" onClick={() => run(false)} disabled={busy || !text.trim()} data-testid="import-preview-btn">
          Preview import
        </button>
        {preview && preview.valid_rows > 0 && (
          <button className="cmd-btn cmd-btn-primary" onClick={() => run(true)} disabled={busy} data-testid="import-confirm-btn">
            Confirm — write {preview.valid_rows} row{preview.valid_rows === 1 ? "" : "s"}
          </button>
        )}
      </div>
      {preview && (
        <div style={{ marginTop: 14 }}>
          <p style={{ fontSize: 13, margin: "0 0 8px" }}>
            <strong>{preview.valid_rows}</strong> valid · <strong className="cmd-danger-text">{preview.error_rows}</strong> with errors
          </p>
          <div className="cmd-import-preview">
            <table className="cmd-table" style={{ width: "100%" }}>
              <thead><tr><th>Row</th><th>Initiative</th><th>Month</th><th>Status</th></tr></thead>
              <tbody>
                {preview.rows.map((r) => (
                  <tr key={r.row} data-testid={`import-row-${r.row}`}>
                    <td>{r.row}</td><td>{r.initiative}</td><td>{r.month}</td>
                    <td>{r.errors.length
                      ? <span className="cmd-import-err">{r.errors.join("; ")}</span>
                      : <span style={{ color: "var(--cmd-health-ok)" }}>OK</span>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

function Shareholders({ isFounder, users }) {
  const [data, setData] = useState(null);
  const [form, setForm] = useState(null); // null closed, {} new, {…} editing
  const load = async () => {
    try { setData((await cmdApi.get("/shareholders")).data); }
    catch { setData({ items: [], total_invested: null }); }
  };
  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    const payload = {
      name: form.name, instrument: form.instrument || null,
      amount_invested: form.amount_invested ? Number(form.amount_invested) : null,
      holding_pct: form.holding_pct ? Number(form.holding_pct) : null,
      since_date: form.since_date || null, notes: form.notes || null,
      linked_user_id: form.linked_user_id || null,
    };
    try {
      if (form.id) await cmdApi.patch(`/shareholders/${form.id}`, payload);
      else await cmdApi.post("/shareholders", payload);
      toast.success("Saved");
      setForm(null); load();
    } catch (ex) { toast.error(ex?.response?.data?.detail || "Could not save."); }
  };

  const remove = async (id) => {
    try { await cmdApi.delete(`/shareholders/${id}`); toast.success("Removed"); load(); }
    catch (ex) { toast.error(ex?.response?.data?.detail || "Could not remove."); }
  };

  if (!data) return null;
  return (
    <div className="cmd-detail-section" data-testid="shareholders-section" style={{ marginTop: 26 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
        <h3 className="cmd-vertical-head" style={{ margin: 0 }}>Shareholders</h3>
        {isFounder && (
          <button className="cmd-btn cmd-btn-ghost" style={{ padding: "5px 12px", fontSize: 13 }}
            onClick={() => setForm({})} data-testid="shareholder-add">
            <Plus size={13} style={{ marginRight: 4 }} />Add shareholder
          </button>
        )}
      </div>
      {data.items.length === 0 ? (
        <p className="cmd-muted" style={{ fontSize: 13 }}>No shareholders recorded yet.</p>
      ) : (
        <div className="cmd-pl-wrap" style={{ marginTop: 10 }}>
          <table className="cmd-pl cmd-sh-table">
            <thead><tr><th>Name</th><th>Instrument</th><th>Invested</th><th>Holding</th><th>Since</th>{isFounder && <th></th>}</tr></thead>
            <tbody>
              {data.items.map((s) => (
                <tr key={s.id} data-testid={`shareholder-${s.id}`}>
                  <td>{s.name}</td>
                  <td>{s.instrument || "—"}</td>
                  <td>{formatINR(s.amount_invested)}</td>
                  <td>{s.holding_pct !== null && s.holding_pct !== undefined ? `${s.holding_pct}%` : "—"}</td>
                  <td>{s.since_date ? fmtDate(s.since_date) : "—"}</td>
                  {isFounder && (
                    <td style={{ textAlign: "right" }}>
                      <button className="cmd-link" style={{ width: "auto", padding: "2px 8px" }}
                        onClick={() => setForm({ ...s, amount_invested: s.amount_invested ?? "", holding_pct: s.holding_pct ?? "", since_date: s.since_date ?? "", instrument: s.instrument ?? "", notes: s.notes ?? "", linked_user_id: s.linked_user_id ?? "" })}
                        data-testid={`shareholder-edit-${s.id}`}>Edit</button>
                      <button className="cmd-link" style={{ width: "auto", padding: "2px 8px", color: "var(--cmd-health-risk)" }}
                        onClick={() => remove(s.id)} data-testid={`shareholder-del-${s.id}`}>Remove</button>
                    </td>
                  )}
                </tr>
              ))}
              <tr className="cmd-pl-total">
                <td>Total invested</td><td></td>
                <td data-testid="shareholders-total">{formatINR(data.total_invested)}</td>
                <td></td><td></td>{isFounder && <td></td>}
              </tr>
            </tbody>
          </table>
        </div>
      )}
      {form && (
        <Sheet onClose={() => setForm(null)} title={form.id ? "Edit shareholder" : "Add shareholder"} testId="shareholder-form">
          <form onSubmit={save}>
            <div className="cmd-field"><label>Name</label>
              <input className="cmd-input" value={form.name ?? ""} required onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} data-testid="sh-name" /></div>
            <div className="cmd-form-grid">
              <div className="cmd-field"><label>Instrument</label>
                <input className="cmd-input" value={form.instrument ?? ""} onChange={(e) => setForm((f) => ({ ...f, instrument: e.target.value }))} data-testid="sh-instrument" /></div>
              <div className="cmd-field"><label>Amount invested (₹)</label>
                <input className="cmd-input" inputMode="decimal" value={form.amount_invested ?? ""} onChange={(e) => setForm((f) => ({ ...f, amount_invested: e.target.value }))} data-testid="sh-amount" /></div>
              <div className="cmd-field"><label>Holding %</label>
                <input className="cmd-input" inputMode="decimal" value={form.holding_pct ?? ""} onChange={(e) => setForm((f) => ({ ...f, holding_pct: e.target.value }))} data-testid="sh-holding" /></div>
              <div className="cmd-field"><label>Since</label>
                <input type="date" className="cmd-input" value={form.since_date ?? ""} onChange={(e) => setForm((f) => ({ ...f, since_date: e.target.value }))} data-testid="sh-since" /></div>
              <div className="cmd-field"><label>Linked user</label>
                <select className="cmd-input" value={form.linked_user_id ?? ""} onChange={(e) => setForm((f) => ({ ...f, linked_user_id: e.target.value }))} data-testid="sh-user">
                  <option value="">None</option>
                  {users.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
                </select></div>
            </div>
            <div className="cmd-field"><label>Notes</label>
              <input className="cmd-input" value={form.notes ?? ""} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} data-testid="sh-notes" /></div>
            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
              <button type="button" className="cmd-btn cmd-btn-ghost" onClick={() => setForm(null)}>Cancel</button>
              <button type="submit" className="cmd-btn cmd-btn-primary" data-testid="sh-save">Save shareholder</button>
            </div>
          </form>
        </Sheet>
      )}
    </div>
  );
}

export default function CmdFinancials() {
  const { can, isFounder, role } = usePermissions();
  const canEdit = can("financials", "edit");
  const canLock = canEdit && ["Founder", "CFO", "Finance Director"].includes(role);
  const [month, setMonth] = useState(null);
  const [scope, setScope] = useState("all");
  const [data, setData] = useState(null);
  const [initiatives, setInitiatives] = useState([]);
  const [users, setUsers] = useState([]);
  const [entryOpen, setEntryOpen] = useState(false);
  const [entryInit, setEntryInit] = useState(null);
  const [importOpen, setImportOpen] = useState(false);
  const [deleteRow, setDeleteRow] = useState(null);         // { initiative_id, name }
  const [wipeOpen, setWipeOpen] = useState(false);

  // Default month: latest month with data, else last month (IST).
  useEffect(() => {
    (async () => {
      try {
        const { data } = await cmdApi.get("/financials/latest-month");
        setMonth(data.month || istLastMonth());
      } catch { setMonth(istLastMonth()); }
    })();
  }, []);

  const load = async () => {
    if (!month) return;
    try {
      const { data } = await cmdApi.get(`/financials?month=${month}&scope=${encodeURIComponent(scope)}`);
      setData(data);
    } catch (ex) {
      toast.error(ex?.response?.data?.detail || "Could not load financials.");
      setData(null);
    }
  };
  useEffect(() => {
    load();
    (async () => {
      try {
        const [{ data: inits }, { data: us }] = await Promise.all([
          cmdApi.get("/initiatives"), cmdApi.get("/users"),
        ]);
        setInitiatives(inits.items || []);
        setUsers(us.items || []);
      } catch { /* non-fatal */ }
    })();
  }, [month, scope]); // eslint-disable-line react-hooks/exhaustive-deps

  const download = async (path, filename) => {
    try {
      const { data } = await cmdApi.get(path, { responseType: "blob" });
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url; a.download = filename; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Download failed."); }
  };

  const toggleLock = async () => {
    try {
      if (data.lock) await cmdApi.post(`/financials/${month}/unlock`);
      else await cmdApi.post(`/financials/${month}/lock`);
      toast.success(data.lock ? "Month unlocked" : "Month locked");
      load();
    } catch (ex) { toast.error(ex?.response?.data?.detail || "Lock failed."); }
  };

  const rowsByVertical = useMemo(() => {
    if (!data) return [];
    const order = [];
    const map = {};
    for (const r of data.rows) {
      if (!map[r.vertical]) { map[r.vertical] = []; order.push(r.vertical); }
      map[r.vertical].push(r);
    }
    return order.map((v) => ({ vertical: v, rows: map[v], sub: data.vertical_subtotals[v] }));
  }, [data]);

  const t = data?.total;
  const prior = data?.prior_month;
  const revDelta = deltaPct(t?.revenue, prior?.revenue);
  const anyEntry = (data?.rows || []).some((r) => r.has_entry);

  const cell = (v, pct) => v === null || v === undefined
    ? <span className="cmd-muted-cell">—</span>
    : <span className={v < 0 ? "neg" : ""}>{pct ? fmtPct(v) : formatINR(v)}</span>;

  if (!month) return null;
  return (
    <section data-testid="cmd-financials">
      <div className="cmd-toolbar" style={{ alignItems: "center" }}>
        <input type="month" className="cmd-input cmd-filter" value={month}
          onChange={(e) => setMonth(e.target.value)} data-testid="fin-month-picker" />
        <select className="cmd-input cmd-filter" value={scope} onChange={(e) => setScope(e.target.value)} data-testid="fin-scope">
          <option value="all">Consolidated</option>
          <optgroup label="Verticals">
            {["Coffee", "Dairy", "Hospitality", "Retail food", "Sri Lanka", "Technology"].map((v) => <option key={v} value={v}>{v}</option>)}
          </optgroup>
          <optgroup label="Initiatives">
            {initiatives.map((i) => <option key={i.id} value={i.id}>{i.name}</option>)}
          </optgroup>
        </select>
        {data?.lock && (
          <span className="cmd-locked-banner" data-testid="fin-locked-banner">
            <Lock size={13} /> {data.month_label} is locked by the {data.lock.by_role}
          </span>
        )}
        <div style={{ marginLeft: "auto", display: "flex", gap: 8, flexWrap: "wrap" }}>
          {canEdit && (
            <button className="cmd-btn cmd-btn-primary" onClick={() => { setEntryInit(null); setEntryOpen(true); }}
              data-testid="enter-figures"><Plus size={15} style={{ marginRight: 5 }} />Enter figures</button>
          )}
          {canEdit && (
            <button className="cmd-btn" onClick={() => setImportOpen(true)} data-testid="import-csv">
              <Upload size={14} style={{ marginRight: 5 }} />Import CSV</button>
          )}
          <button className="cmd-btn cmd-btn-ghost" onClick={() => download("/financials/template.csv", "eepl-financials-template.csv")}
            data-testid="csv-template"><Download size={14} style={{ marginRight: 5 }} />CSV template</button>
          <button className="cmd-btn cmd-btn-ghost" onClick={() => download(`/financials/${month}/export.csv`, `eepl-financials-${month}.csv`)}
            data-testid="export-month"><Download size={14} style={{ marginRight: 5 }} />Export month</button>
          {canLock && (
            <button className="cmd-btn cmd-btn-ghost" onClick={toggleLock} data-testid="lock-month">
              {data?.lock ? <><Unlock size={14} style={{ marginRight: 5 }} />Unlock month</> : <><Lock size={14} style={{ marginRight: 5 }} />Lock month</>}
            </button>
          )}
          {isFounder && anyEntry && !data?.lock && (
            <button className="cmd-btn cmd-btn-ghost" onClick={() => setWipeOpen(true)}
              style={{ color: "var(--cmd-health-risk)" }} data-testid="wipe-month">
              <Trash2 size={14} style={{ marginRight: 5 }} />Wipe month…
            </button>
          )}
        </div>
      </div>

      {!data ? (
        <p className="cmd-muted">Loading…</p>
      ) : !anyEntry ? (
        <div className="cmd-empty" data-testid="fin-empty">
          <h3>No figures for {data.month_label} yet</h3>
          <p>Enter them or import a CSV from your books.</p>
        </div>
      ) : (
        <>
          <div className="cmd-kpis8">
            <Kpi label={`Revenue · ${data.month_label}`} value={formatINR(t.revenue)} testId="kpi-revenue"
              sub={revDelta !== null ? `${revDelta >= 0 ? "▲" : "▼"} ${Math.abs(revDelta * 100).toFixed(1)}% vs last month` : "No prior month"}
              subNeg={revDelta !== null && revDelta < 0} />
            <Kpi label="Gross margin" value={fmtPct(t.gm_pct)} testId="kpi-gm" />
            <Kpi label="EBITDA" value={formatINR(t.ebitda)} testId="kpi-ebitda"
              sub={t.budget_variance !== null && t.budget_variance !== undefined
                ? `${t.budget_variance >= 0 ? "+" : "−"}${formatINR(Math.abs(t.budget_variance))} vs budget`
                : t.ebitda !== null && t.ebitda < 0 ? "Negative" : undefined}
              subNeg={(t.budget_variance ?? 0) < 0 || (t.ebitda ?? 0) < 0} />
            <Kpi label="EBITDA margin" value={fmtPct(t.ebitda_pct)} testId="kpi-ebitda-pct" />
            <Kpi label="Cash balance" value={formatINR(t.cash_balance)} testId="kpi-cash" />
            <Kpi label="Receivables" value={formatINR(t.receivables)} testId="kpi-receivables" />
            <Kpi label="Payables" value={formatINR(t.payables)} testId="kpi-payables" />
            <Kpi label={`${data.fy} to date`} value={formatINR(data.fytd.revenue)} testId="kpi-fytd"
              sub={`EBITDA ${formatINR(data.fytd.ebitda)}`} subNeg={(data.fytd.ebitda ?? 0) < 0} />
          </div>

          <div className="cmd-chart-wrap" data-testid="fin-trend">
            <h3>Last 12 months — revenue vs EBITDA</h3>
            <ResponsiveContainer width="100%" height={220}>
              <ComposedChart data={data.trend} margin={{ top: 4, right: 8, bottom: 0, left: 8 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--cmd-line)" vertical={false} />
                <XAxis dataKey="month" tick={{ fontSize: 11, fill: "var(--cmd-muted)" }}
                  tickFormatter={(m) => m.slice(2)} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 11, fill: "var(--cmd-muted)" }} axisLine={false} tickLine={false}
                  tickFormatter={(v) => formatINR(v)} width={70} />
                <Tooltip formatter={(v, name) => [formatINR(v), name === "revenue" ? "Revenue" : "EBITDA"]}
                  contentStyle={{ background: "var(--cmd-raised)", border: "1px solid var(--cmd-line)", borderRadius: 10, fontSize: 13 }} />
                <Bar dataKey="revenue" fill="var(--cmd-accent)" radius={[4, 4, 0, 0]} maxBarSize={26} />
                <Line type="monotone" dataKey="ebitda" stroke="var(--cmd-ink)" strokeWidth={2}
                  dot={<EbitdaDot />} connectNulls />
              </ComposedChart>
            </ResponsiveContainer>
          </div>

          <div className="cmd-pl-wrap" data-testid="fin-pl">
            <table className="cmd-pl">
              <thead>
                <tr><th>Initiative</th><th>Revenue</th><th>GM %</th><th>Opex</th><th>EBITDA</th><th>EBITDA %</th><th>vs budget</th></tr>
              </thead>
              <tbody>
                {rowsByVertical.map(({ vertical, rows, sub }) => (
                  <VerticalBlock key={vertical} vertical={vertical} rows={rows} sub={sub}
                    onRow={(r) => { setEntryInit(r.initiative_id); setEntryOpen(true); }} />
                ))}
                <tr className="cmd-pl-total" data-testid="fin-total-row">
                  <td>Total · {data.month_label}</td>
                  <td>{cell(t.revenue)}</td><td>{cell(t.gm_pct, true)}</td><td>{cell(t.opex)}</td>
                  <td>{cell(t.ebitda)}</td><td>{cell(t.ebitda_pct, true)}</td>
                  <td>{t.budget_variance === null || t.budget_variance === undefined ? "—" : cell(t.budget_variance)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </>
      )}

      <Shareholders isFounder={isFounder} users={users} />

      {entryOpen && (
        <Sheet onClose={() => setEntryOpen(false)} title={canEdit ? "Enter figures" : "Figures"} testId="fin-entry-sheet" wide>
          <EntryForm initiatives={initiatives} initId={entryInit} month={month} canEdit={canEdit && !data?.lock}
            onSaved={() => { setEntryOpen(false); load(); }} onClose={() => setEntryOpen(false)}
            onDelete={isFounder ? (iid, name) => { setEntryOpen(false); setDeleteRow({ initiative_id: iid, name }); } : null} />
        </Sheet>
      )}
      {importOpen && (
        <Sheet onClose={() => setImportOpen(false)} title="Import CSV" testId="import-sheet-modal" wide>
          <ImportSheet month={month} onDone={() => { setImportOpen(false); load(); }} onClose={() => setImportOpen(false)} />
        </Sheet>
      )}
      {deleteRow && (
        <Sheet onClose={() => setDeleteRow(null)} title="Remove this row of figures" testId="fin-delete-sheet">
          <DeleteWithReason
            onCancel={() => setDeleteRow(null)}
            onConfirm={async (reason) => {
              try {
                await cmdApi.delete(`/financials/${deleteRow.initiative_id}/${month}`, { data: { reason } });
                toast.success("Row removed · reason recorded in audit log");
                setDeleteRow(null); load();
              } catch (ex) {
                toast.error(ex?.response?.data?.detail || "Could not remove.");
              }
            }}
            summary={<>You are removing <strong>{deleteRow.name}</strong> for <strong>{data?.month_label}</strong>. This is permanent and will be captured in the audit log along with your reason.</>}
          />
        </Sheet>
      )}
      {wipeOpen && (
        <Sheet onClose={() => setWipeOpen(false)} title={`Wipe every row for ${data?.month_label}`} testId="fin-wipe-sheet">
          <WipeMonthForm
            monthLabel={data?.month_label || ""}
            onCancel={() => setWipeOpen(false)}
            onConfirm={async ({ reason, confirmLabel }) => {
              try {
                const res = await cmdApi.post(`/financials/month/${month}/wipe`, {
                  reason, confirm_month_label: confirmLabel,
                });
                toast.success(`Wiped ${res.data.deleted} row${res.data.deleted === 1 ? "" : "s"} · reason recorded`);
                setWipeOpen(false); load();
              } catch (ex) {
                toast.error(ex?.response?.data?.detail || "Could not wipe.");
              }
            }}
          />
        </Sheet>
      )}
    </section>
  );
}

function DeleteWithReason({ onCancel, onConfirm, summary }) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const ok = reason.trim().length >= 10;
  return (
    <div>
      <div className="cmd-danger-box" data-testid="delete-summary">{summary}</div>
      <div className="cmd-field" style={{ marginTop: 14 }}>
        <label>Reason (required, at least 10 characters)</label>
        <textarea className="cmd-input" rows={3} value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="Why is this being removed? e.g. duplicate CSV import, wrong initiative, corrected by …"
          data-testid="delete-reason" />
      </div>
      <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 12 }}>
        <button className="cmd-btn cmd-btn-ghost" onClick={onCancel} disabled={busy}>Cancel</button>
        <button className="cmd-btn cmd-btn-primary" disabled={!ok || busy}
          style={{ background: "var(--cmd-health-risk)" }}
          onClick={async () => { setBusy(true); try { await onConfirm(reason.trim()); } finally { setBusy(false); } }}
          data-testid="delete-confirm">
          {busy ? "Removing…" : "Confirm — remove"}
        </button>
      </div>
    </div>
  );
}

function WipeMonthForm({ monthLabel, onCancel, onConfirm }) {
  const [reason, setReason] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const ok = reason.trim().length >= 10 && confirm.trim().toLowerCase() === monthLabel.toLowerCase();
  return (
    <div data-testid="wipe-form">
      <div className="cmd-danger-box">
        You are about to <strong>wipe every initiative's figures for {monthLabel}</strong>. This cannot be undone from the UI
        (the audit log keeps a record for restore via mongorestore). Only the Founder can do this.
      </div>
      <div className="cmd-field" style={{ marginTop: 14 }}>
        <label>Reason (required, at least 10 characters)</label>
        <textarea className="cmd-input" rows={3} value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="Why is this being wiped? e.g. duplicate import, wrong template used, re-importing from Tally…"
          data-testid="wipe-reason" />
      </div>
      <div className="cmd-field">
        <label>Retype the month label to confirm — <strong>{monthLabel}</strong></label>
        <input className="cmd-input" value={confirm} onChange={(e) => setConfirm(e.target.value)}
          placeholder={monthLabel} data-testid="wipe-confirm-label" />
      </div>
      <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 12 }}>
        <button className="cmd-btn cmd-btn-ghost" onClick={onCancel} disabled={busy}>Cancel</button>
        <button className="cmd-btn cmd-btn-primary" disabled={!ok || busy}
          style={{ background: "var(--cmd-health-risk)" }}
          onClick={async () => { setBusy(true); try { await onConfirm({ reason: reason.trim(), confirmLabel: confirm.trim() }); } finally { setBusy(false); } }}
          data-testid="wipe-confirm">
          {busy ? "Wiping…" : "Confirm — wipe month"}
        </button>
      </div>
    </div>
  );
}

function VerticalBlock({ vertical, rows, sub, onRow }) {
  return (
    <>
      <tr className="cmd-pl-subtotal"><td colSpan={7} style={{ textAlign: "left" }}>{vertical}</td></tr>
      {rows.map((r) => (
        <tr key={r.initiative_id} className="cmd-pl-init" onClick={() => onRow(r)} data-testid={`fin-row-${r.initiative_id}`}>
          <td>{r.name}{!r.has_entry && <span className="cmd-muted-cell"> · no entry</span>}</td>
          <td>{r.revenue === null || r.revenue === undefined ? <span className="cmd-muted-cell">—</span> : formatINR(r.revenue)}</td>
          <td>{fmtPctCell(r.gm_pct)}</td>
          <td>{r.opex === null || r.opex === undefined ? <span className="cmd-muted-cell">—</span> : formatINR(r.opex)}</td>
          <td className={r.ebitda < 0 ? "neg" : ""}>{r.ebitda === null || r.ebitda === undefined ? <span className="cmd-muted-cell">—</span> : formatINR(r.ebitda)}</td>
          <td>{fmtPctCell(r.ebitda_pct)}</td>
          <td>{r.budget_variance === null || r.budget_variance === undefined ? <span className="cmd-muted-cell">—</span>
            : <span className={r.budget_variance < 0 ? "neg" : ""}>{formatINR(r.budget_variance)}</span>}</td>
        </tr>
      ))}
      {sub && rows.length > 1 && (
        <tr className="cmd-pl-subtotal" data-testid={`fin-subtotal-${vertical.replace(/\s/g, "-")}`}>
          <td>{vertical} subtotal</td>
          <td>{formatINR(sub.revenue)}</td><td>{fmtPctCell(sub.gm_pct)}</td><td>{formatINR(sub.opex)}</td>
          <td className={sub.ebitda < 0 ? "neg" : ""}>{formatINR(sub.ebitda)}</td>
          <td>{fmtPctCell(sub.ebitda_pct)}</td>
          <td>{sub.budget_variance === null || sub.budget_variance === undefined ? "—" : formatINR(sub.budget_variance)}</td>
        </tr>
      )}
    </>
  );
}

function fmtPctCell(v) {
  return v === null || v === undefined ? <span className="cmd-muted-cell">—</span> : <span>{fmtPct(v)}</span>;
}
